import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, catchError, map } from 'rxjs';
import { IRequest, IRequestOptions, IApiConfig } from '../interfaces';
import { ISnackbarMessage, NHASnackbarService } from '@nha/components';
import { MatSnackBarConfig } from '@angular/material/snack-bar';

export abstract class HttpService {
	protected serviceUrl: string;
	protected http: HttpClient;

	constructor(
		protected config: IApiConfig,
		private _endPoint: string,
		protected snackbarService: NHASnackbarService = null
	) {
		this.serviceUrl = `${this.config.getBaseUrl()}${this._endPoint}`;
		this.http = this.config.getHttpClient();
	}

	get<T>(
		url: string,
		options?: {
			snackbarErrorMessage?: string;
			snackbarConfig?: MatSnackBarConfig<ISnackbarMessage>;
			headers?:
				| HttpHeaders
				| {
						[header: string]: string | string[];
				  };
			context?: HttpContext;
			observe?: 'body';
			params?:
				| HttpParams
				| {
						[param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
				  };
			reportProgress?: boolean;
			responseType?: 'json';
			withCredentials?: boolean;
		}
	): Observable<T> {
		return this.http.get<T>(url, { ...options }).pipe(
			map((response: T) => response),
			catchError((err: HttpErrorResponse) => {
				if (options?.snackbarErrorMessage && this.snackbarService) {
					this.snackbarService.queueMessage(() =>
						this.snackbarService.error(options.snackbarErrorMessage, 'Dismiss', { duration: 2000, ...options.snackbarConfig })
					);
				}

				throw err;
			})
		);
	}

	/**
	 * GET a binary body (a file download). Separate from `get` because that wrapper pins
	 * `responseType: 'json'` in its type — this keeps blob downloads on the same snackbar-on-error
	 * contract instead of every service hand-rolling its own around the raw client.
	 */
	getBlob(
		url: string,
		options?: {
			snackbarErrorMessage?: string;
			snackbarConfig?: MatSnackBarConfig<ISnackbarMessage>;
		}
	): Observable<Blob> {
		return this.http.get(url, { responseType: 'blob' }).pipe(
			catchError((err: HttpErrorResponse) => {
				if (options?.snackbarErrorMessage && this.snackbarService) {
					this.snackbarService.queueMessage(() =>
						this.snackbarService.error(options.snackbarErrorMessage, 'Dismiss', { duration: 2000, ...options.snackbarConfig })
					);
				}

				throw err;
			})
		);
	}

	getByID<T>(id: number): Observable<T> {
		const url: string = this.serviceUrl;
		return this.http.get<T>(`${url}/${id}`);
	}

	getAll<T>(): Observable<T[]> {
		const url: string = this.serviceUrl;
		return this.http.get<T[]>(url);
	}

	post<T>(
		url: string,
		body: unknown,
		options?: {
			snackbarErrorMessage?: string;
			snackbarConfig?: MatSnackBarConfig<ISnackbarMessage>;
			headers?:
				| HttpHeaders
				| {
						[header: string]: string | string[];
				  };
			context?: HttpContext;
			observe?: 'body';
			params?:
				| HttpParams
				| {
						[param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
				  };
			reportProgress?: boolean;
			responseType?: 'json';
			withCredentials?: boolean;
		}
	): Observable<T> {
		return this.http.post<T>(url, body, { ...options }).pipe(
			map((response: T) => response),
			catchError((err: HttpErrorResponse) => {
				if (options?.snackbarErrorMessage && this.snackbarService) {
					this.snackbarService.queueMessage(() =>
						this.snackbarService.error(options.snackbarErrorMessage, 'Dismiss', { duration: 2000, ...options.snackbarConfig })
					);
				}

				throw err;
			})
		);
	}

	postBody<T>(entity: T): Observable<T> {
		const url: string = this.serviceUrl;
		return this.http.post<T>(url, entity);
	}

	postAll<T>(entities: T[]): Observable<HttpResponse<object>> {
		const url: string = `${this.serviceUrl}/postall`;
		return this.http.post<object>(url, entities, { observe: 'response' });
	}

	put<T>(
		url: string,
		body: unknown,
		options?: {
			snackbarErrorMessage?: string;
			snackbarConfig?: MatSnackBarConfig<ISnackbarMessage>;
			headers?:
				| HttpHeaders
				| {
						[header: string]: string | string[];
				  };
			context?: HttpContext;
			observe?: 'body';
			params?:
				| HttpParams
				| {
						[param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
				  };
			reportProgress?: boolean;
			responseType?: 'json';
			withCredentials?: boolean;
		}
	): Observable<T> {
		return this.http.put<T>(url, body, { ...options }).pipe(
			map((response: T) => response),
			catchError((err: HttpErrorResponse) => {
				if (options?.snackbarErrorMessage && this.snackbarService) {
					this.snackbarService.queueMessage(() =>
						this.snackbarService.error(options.snackbarErrorMessage, 'Dismiss', { duration: 2000, ...options.snackbarConfig })
					);
				}

				throw err;
			})
		);
	}

	putBody<T>(entity: T, id: number): Observable<HttpResponse<T>> {
		const url: string = `${this.serviceUrl}/${id}`;
		return this.http.put<T>(url, entity, { observe: 'response' });
	}

	putAll<T>(entities: T[]): Observable<HttpResponse<object>> {
		const url: string = `${this.serviceUrl}/putall`;
		return this.http.put<object>(url, entities, { observe: 'response' });
	}

	delete<T>(
		url: string,
		options?: {
			snackbarErrorMessage?: string;
			snackbarConfig?: MatSnackBarConfig<ISnackbarMessage>;
			headers?:
				| HttpHeaders
				| {
						[header: string]: string | string[];
				  };
			context?: HttpContext;
			observe?: 'body';
			params?:
				| HttpParams
				| {
						[param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
				  };
			reportProgress?: boolean;
			responseType?: 'json';
			withCredentials?: boolean;
			body?: unknown;
		}
	): Observable<T> {
		return this.http.delete<T>(url, { ...options }).pipe(
			map((response: T) => response),
			catchError((err: HttpErrorResponse) => {
				if (options?.snackbarErrorMessage && this.snackbarService) {
					this.snackbarService.queueMessage(() =>
						this.snackbarService.error(options.snackbarErrorMessage, 'Dismiss', { duration: 2000, ...options.snackbarConfig })
					);
				}

				throw err;
			})
		);
	}

	deleteByID(id: number): Observable<HttpResponse<object>> {
		const url: string = `${this.serviceUrl}/${id}`;
		return this.http.delete<object>(url, { observe: 'response' });
	}

	deleteAll(ids: string[] | number[]): Observable<HttpResponse<object>> {
		const url: string = `${this.serviceUrl}/deleteall`;
		return this.http.post<object>(url, ids, { observe: 'response' });
		// use a post to put the ids in the body
	}

	request<T>(request?: IRequest): Observable<T> {
		/** default settings */
		let requestOptions: IRequestOptions = {
			url: this.serviceUrl,
			method: 'GET'
		};

		/** check for request options */
		if (request && request !== null) {
			/** check for request options */
			if (request.options && request.options !== null) {
				requestOptions = Object.assign(requestOptions, request.options);
			}

			/** check for urlAppendStr */
			if (request.urlAppendStr && request.urlAppendStr !== null && request.urlAppendStr.length > 0) {
				requestOptions.url += request.urlAppendStr;
			}

			/** check for params */
			if (request.params && request.params !== null) {
				/** create the params string */
				const paramsStr = request.params.toString();
				if (!requestOptions.url.includes('?')) {
					requestOptions.url += '?';
				}

				/** append the string to the url */
				requestOptions.url += paramsStr;
			}
		}

		/** make the request */
		return this.http.request(requestOptions.method, requestOptions.url, requestOptions);
	}
}
